import {Component, computed, inject, input, OnDestroy, OnInit, signal} from '@angular/core';
import {AbstractControl, ControlValueAccessor, FormArray, FormControl, FormGroup, NgControl} from '@angular/forms';
import {Subject, takeUntil} from 'rxjs';
import {ValidationError} from '@shared/field/abstract-field.component';

@Component({
  selector: 'app-field-base',
  imports: [],
  template: ''
})
export class FieldBaseComponent<valueT> implements ControlValueAccessor, OnInit, OnDestroy {

  public readonly label = input<string | null>(null);
  public readonly hint = input<string | null>(null);
  public readonly hintMode = input<'toggle' | 'always'>('always');
  public readonly optional = input<boolean>(false);
  public readonly description = input<string | null>(null);
  public readonly placeholder = input<string | null>(null);
  public readonly readonly = input<boolean>(false);
  public readonly dependencies = input<AbstractControl | AbstractControl[]>();
  public readonly dependants = input<AbstractControl[]>([]);
  public readonly enableIf = input<(control: AbstractControl[]) => boolean>();

  // Internal State as a Signal
  protected readonly value = signal<valueT | null>(null);
  public readonly errors = signal<ValidationError[]>([]);
  public readonly hasErrors = computed(() => this.errors().length > 0);
  public readonly disabled = signal<boolean>(false);
  public readonly enabled = computed(() => !this.disabled());

  // CVA Callbacks
  protected onChange = (value: valueT | null) => {};
  protected onTouched = () => {};

  public readonly ngControl = inject(NgControl, {
    self: true,
    optional: false
  });

  public control: FormControl<valueT>;

  protected readonly destroy$ = new Subject<void>();

  constructor() {
    this.ngControl.valueAccessor = this;
  }

  public ngOnInit(): void {
    this.control = this.ngControl.control as FormControl;
    this.fetchErrors();
    this.control.statusChanges.pipe(
      takeUntil(this.destroy$)
    ).subscribe(_status => {
      this.fetchErrors();
    });
    this.control.events.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      this.fetchErrors();
    });
    const rootForm = this.getRootForm();
    if (rootForm) {
      rootForm.events.pipe(takeUntil(this.destroy$)).subscribe(event => {
        this.fetchErrors();
      });
    }

    const dependants = this.dependants();
    const dependantControls = dependants
      ? (Array.isArray(dependants) ? dependants : [dependants])
      : [];

    if (dependantControls.length > 0) {
      this.control.valueChanges.pipe(takeUntil(this.destroy$)).subscribe(_value => {
        for (const dependent of dependantControls) {
          dependent.updateValueAndValidity();
        }
      });
    }

    const deps = this.dependencies();
    const dependencies = deps
      ? (Array.isArray(deps) ? deps : [deps])
      : [];
    if (dependencies.length > 0) {
      dependencies.forEach(dependency => {
        dependency.statusChanges.pipe(
          takeUntil(this.destroy$)
        ).subscribe(_status => {
          this.refreshDependencies();
        });
      });
    }

    const hasEnableCond = !!this.enableIf();
    if (hasEnableCond) {
      if (dependencies.length === 0) {
        console.warn(`enableIf without dependencies (dependsOn).`);
      } else {
        dependencies.forEach(dependency => {
          dependency.valueChanges.pipe(
            takeUntil(this.destroy$)
          ).subscribe(_status => {
            this.refreshDependencies();
          });
        });
      }
    }
    this.refreshDependencies();
  }

  public setValue(value: valueT | null) {
    if (!this.disabled()) {
      this.value.set(value);
      this.onChange(value);
      this.onTouched();
    }
  }

  // --- ControlValueAccessor Interface Methods ---
  public writeValue(val: valueT | null): void {
    this.value.set(val);
  }

  public registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  public ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  protected fetchErrors(): void {
    const validationErrors = this.control.errors;
    const errors = this.control.disabled || !validationErrors
      ? []
      : Object.entries(validationErrors)
        .map(([key, error]) => ({key: key, error: error}));
    this.errors.set(errors);
  }

  private getRootForm(): FormGroup | FormArray | null {
    let parent: FormGroup | FormArray | null = this.control.parent;
    while (parent) {
      if (!parent.parent) {
        return parent;
      }
      parent = parent.parent;
    }
    return null;
  }

  private refreshDependencies(): void {
    const dependencies = this.dependencies();
    const dependencyControls = dependencies
      ? (Array.isArray(dependencies) ? dependencies : [dependencies])
      : [];
    if (dependencyControls.length > 0) {
      const enableIf = this.enableIf();
      const enable = enableIf
        ? enableIf(dependencyControls)
        : dependencyControls.some((dep => dep.valid));
      if (enable) {
        this.control.enable();
      } else {
        this.control.disable();
      }
    }
  }

  // private refreshDependency(): void {
  //   const dependsOn = this.dependsOn();
  //   const dependencies = dependsOn
  //     ? (Array.isArray(dependsOn) ? dependsOn : [dependsOn])
  //     : [];
  //   const enableCond = this.enableIf();
  //   if (dependencies.length > 0) {
  //     const enable = dependencies.some(dep => enableCond ? enableCond(dep) : !!dep?.valid);
  //     if (enable) {
  //       this.control.enable();
  //     } else {
  //       this.control.disable();
  //     }
  //   }
  // }

}
